'use client';
import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { useAuth } from '@/contexts/AuthContext';

const HeroSection = () => {
  const router = useRouter();
  const { user, loading } = useAuth();

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals.length) return;

    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals.forEach((el) => observer.observe(el));
  }, []);

  const handleGetStarted = async () => {
    if (loading) return;

    if (!user) {
      // Redirect to signup with return URL
      router.push('/sign-up-login?redirect=checkout');
    } else {
      // User is authenticated, redirect to checkout
      try {
        const response = await fetch('/api/create-checkout-session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });

        const data = await response.json();

        if (data.url) {
          window.location.href = data.url;
        } else {
          console.error('Failed to create checkout session:', data.error);
          alert('Failed to start checkout. Please try again.');
        }
      } catch (error) {
        console.error('Checkout error:', error);
        alert('An error occurred. Please try again.');
      }
    }
  };

  const floatingCards = [
  {
    id: 'float_question',
    title: 'Sample Question',
    content: 'What is the standard rate of turn for an aircraft?',
    icon: 'QuestionMarkCircleIcon'
  },
  {
    id: 'float_stats',
    title: 'Your Progress',
    content: '78% Complete',
    icon: 'ChartBarIcon'
  },
  {
    id: 'float_timer',
    title: 'Study Mode',
    content: '45:00 Remaining',
    icon: 'ClockIcon'
  }];


  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-background py-24">
      {/* Grid Background */}
      <div className="absolute inset-0 grid-bg opacity-50" />
      
      {/* Gradient Overlays */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />

      <div className="container relative z-10 px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left Content */}
          <div className="lg:col-span-7 reveal">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full border border-border bg-card/50 backdrop-blur-sm">
              <Icon name="AcademicCapIcon" size={16} className="text-primary" />
              <span className="text-sm font-medium text-muted-foreground">Aviation Test Preparation</span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-foreground tracking-tight leading-[1.1] mb-6">
              Master Aviation
              <span className="block text-primary">Knowledge</span>
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed mb-10 max-w-2xl">
              Comprehensive study platform with customizable sessions, detailed explanations, and flexible test modes. Prepare for your aviation exams with confidence.
            </p>

            <button
              onClick={handleGetStarted}
              disabled={loading}
              className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base font-semibold text-primary-foreground bg-primary rounded-full hover:bg-primary/90 transition-all shadow-xl hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto">
              {loading ? 'Loading...' : user ? 'Subscribe Now' : 'Start Learning'}
              <Icon name="ArrowRightIcon" size={20} className="ml-2" />
            </button>

            {/* Stats Row */}
            <div className="flex flex-wrap gap-6 sm:gap-8 mt-12 justify-center sm:justify-start">
              <div className="reveal stagger-1">
                <div className="text-3xl font-bold text-foreground">10K+</div>
                <div className="text-sm text-muted-foreground">Active Students</div>
              </div>
              <div className="reveal stagger-2">
                <div className="text-3xl font-bold text-foreground">500+</div>
                <div className="text-sm text-muted-foreground">Questions</div>
              </div>
              <div className="reveal stagger-3">
                <div className="text-3xl font-bold text-foreground">95%</div>
                <div className="text-sm text-muted-foreground">Pass Rate</div>
              </div>
            </div>
          </div>

          {/* Right - Floating Cards */}
          <div className="lg:col-span-5 relative h-[400px] sm:h-[500px] reveal stagger-2">
            {/* Main Image Container */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative w-64 h-64 sm:w-80 sm:h-80 rounded-full overflow-hidden border-4 border-border shadow-2xl">
                <AppImage
                  src="https://images.unsplash.com/photo-1669963732599-c0ed7d720a3e"
                  alt="Aviation cockpit with flight instruments and controls"
                  className="w-full h-full object-cover" />
                
                <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent" />
              </div>
            </div>

            {/* Floating Card 1 - Top Left */}
            <div className="absolute top-0 left-0 w-56 sm:w-64 p-3 sm:p-4 bg-card/90 backdrop-blur-md rounded-xl border border-border shadow-lg animate-fade-slide-in stagger-3 mb-0 pb-3 sm:pb-4">
              <div className="flex items-start gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-primary/10 rounded-lg">
                  <Icon name={floatingCards[0].icon as any} size={18} className="text-primary sm:w-5 sm:h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-muted-foreground mb-1">{floatingCards[0].title}</div>
                  <div className="text-xs sm:text-sm font-semibold text-foreground truncate">{floatingCards[0].content}</div>
                </div>
              </div>
            </div>

            {/* Floating Card 2 - Top Right */}
            <div className="absolute top-16 sm:top-20 right-0 w-48 sm:w-56 p-3 sm:p-4 bg-card/90 backdrop-blur-md rounded-xl border border-border shadow-lg animate-fade-slide-in stagger-4">
              <div className="flex items-start gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-success/10 rounded-lg">
                  <Icon name={floatingCards[1].icon as any} size={18} className="text-success sm:w-5 sm:h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-muted-foreground mb-1">{floatingCards[1].title}</div>
                  <div className="text-xs sm:text-sm font-semibold text-foreground">{floatingCards[1].content}</div>
                </div>
              </div>
            </div>

            {/* Floating Card 3 - Bottom */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-52 sm:w-60 p-3 sm:p-4 bg-card/90 backdrop-blur-md rounded-xl border border-border shadow-lg animate-fade-slide-in stagger-5">
              <div className="flex items-start gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-accent/10 rounded-lg">
                  <Icon name={floatingCards[2].icon as any} size={18} className="text-accent sm:w-5 sm:h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-muted-foreground mb-1">{floatingCards[2].title}</div>
                  <div className="text-xs sm:text-sm font-semibold text-foreground">{floatingCards[2].content}</div>
                </div>
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute top-1/4 left-1/4 text-primary/20 text-4xl">+</div>
            <div className="absolute bottom-1/4 right-1/4 text-accent/20 text-4xl">+</div>
          </div>
        </div>
      </div>
    </section>);

};

export default HeroSection;